import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "../auth";
import { MessageType } from "../types/websocket";
import { useQueryClient } from "@tanstack/react-query";
import { websocketService } from "../services/websocketService";

interface WebSocketContextValue {
  isConnected: boolean;
  activeSyncs: Map<number, any>;
  messages: any[];
  setMessages: (messages: any[]) => void;
  error: string | null;
  sendMessage: (message: any) => void;
  sendMessageAsync: (message: any) => Promise<void>;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const WebSocketProvider = ({ children }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [activeSyncs, setActiveSyncs] = useState<Map<number, any>>(new Map());
  const [messages, setMessages] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize the singleton service ONCE on mount
  useEffect(() => {
    console.log("🔍 [WebSocketProvider] Mounting - initializing singleton service");

    // Initialize with getToken function
    websocketService.initialize(getToken);

    // Subscribe to WebSocket events
    const unsubscribe = websocketService.subscribe({
      onOpen: () => {
        console.log("[WebSocketProvider] Connected");
        setIsConnected(true);
        setError(null);
      },
      onMessage: (data) => {
        console.log("[WebSocketProvider] Received:", data);

        // Handle sync messages - update Map like iOS does
        if (data.type === "sync") {
          const apiKeyId = data.apiKey?.id;
          if (apiKeyId) {
            setActiveSyncs((prev) => {
              const updated = new Map(prev);

              if (data.status === "processing") {
                // Update or add active sync
                updated.set(apiKeyId, data);
              } else if (data.status === "completed" || data.status === "failed") {
                // Keep for 3s then remove
                updated.set(apiKeyId, data);
                setTimeout(() => {
                  setActiveSyncs((current) => {
                    const next = new Map(current);
                    next.delete(apiKeyId);
                    return next;
                  });
                }, 3000);
              }

              return updated;
            });
          }
        }

        // Also keep messages array for backwards compatibility (GlobalAlerts, useSyncStateMachine)
        // Keep only last 50 to avoid memory bloat
        setMessages((prev) => {
          const updated = [...prev, data];
          return updated.length > 50 ? updated.slice(-50) : updated;
        });

        // Handle inbox new-message events
        if (data.type === "inbox:new-message") {
          queryClient.invalidateQueries({ queryKey: ["inbox"] });
          queryClient.invalidateQueries({ queryKey: ["inbox", "unread"] });
          queryClient.invalidateQueries({ queryKey: ["admin-inbox"] });
          return;
        }

        // Update to React Query if needed
        const requestMessage = data?.requestMessage;
        if (!requestMessage) return;
        switch (requestMessage?.type) {
          case MessageType.DOWNLOAD_TAX_REPORT:
            queryClient.setQueryData(
              [
                MessageType.DOWNLOAD_TAX_REPORT,
                requestMessage?.payload?.year?.toString(),
                requestMessage?.payload?.mode,
              ],
              data
            );
            return;
          case MessageType.POSITION:
            queryClient.setQueryData(
              requestMessage?.payload?.exchangeId
                ? [MessageType.POSITION, requestMessage.payload.exchangeId]
                : [MessageType.POSITION],
              data
            );
            return;
          case MessageType.TAX_LOSS_HARVESTING:
            queryClient.setQueryData([MessageType.TAX_LOSS_HARVESTING], data);
            return;
          default:
            break;
        }
      },
      onError: (event) => {
        console.error("[WebSocketProvider] Error:", event);
        setError("WebSocket error");
      },
      onClose: (event) => {
        console.log(`[WebSocketProvider] Disconnected with code ${event.code}: ${event.reason}`);
        setIsConnected(false);

        if (event.code === 1008 || event.code === 1003) {
          setError(event.reason || "Connection rejected by server");
        }
      }
    });

    // Cleanup on unmount only
    return () => {
      console.log("🔍 [WebSocketProvider] Unmounting, unsubscribing");
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount/unmount

  const sendMessage = useCallback((message: any) => {
    websocketService.send(message);
  }, []);

  const sendMessageAsync = useCallback(async (message: any) => {
    websocketService.send(message);
  }, []);

  const value: WebSocketContextValue = {
    isConnected,
    activeSyncs,
    messages,
    setMessages,
    error,
    sendMessage,
    sendMessageAsync,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within WebSocketProvider");
  }
  return context;
};
